import { Injectable } from '@angular/core';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})

export class Ga4Service {

  constructor() { }

  /**
   * Send a general event to GA4
   * @param eventName - The name of the GA4 event (e.g., 'add_to_cart')
   * @param eventParams - Object with event parameters
   */
  public sendEvent(eventName: string, eventParams: { [key: string]: any } = {}): void {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, eventParams);
    } else {
      console.warn('GA4 is not initialized');
    }
  }

  /**
   * Track a page view (SPA-friendly)
   */
  public sendPageView(path: string, title?: string): void {
    this.sendEvent('page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: title || document.title
    });
  }

  /**
   * Track e-commerce purchase event
   * @param transactionData - Transaction and item details
   */
  public sendPurchaseEvent(transactionData: {
    transaction_id: string;
    value: number;
    tax?: number;
    shipping?: number;
    currency?: string;
    coupon?: string;
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity: number;
      item_category?: string;
    }>;
  }): void {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'purchase', {
        transaction_id: transactionData.transaction_id,
        value: transactionData.value,
        tax: transactionData.tax || 0,
        shipping: transactionData.shipping || 0,
        currency: transactionData.currency || 'USD',
        coupon: transactionData.coupon || '',
        items: transactionData.items
      });
    } else {
      console.warn('GA4 is not initialized for purchase event');
    }
  }

  /**
   * Track begin_checkout event
   */
  public sendBeginCheckoutEvent(checkoutData: {
    currency?: string;
    value: number;
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity: number;
    }>;
  }): void {
    this.sendEvent('begin_checkout', {
      currency: checkoutData.currency || 'USD',
      value: checkoutData.value,
      items: checkoutData.items
    });
  }

  /**
   * Track view_cart event
   */
  public sendViewCartEvent(cartData: {
    currency?: string;
    value: number;
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity: number;
    }>;
  }): void {
    this.sendEvent('view_cart', {
      currency: cartData.currency || 'USD',
      value: cartData.value,
      items: cartData.items
    });
  }

  /**
   * Track add_to_cart event
   */
  public sendAddToCartEvent(itemData: {
    currency?: string;
    value: number;
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity: number;
      item_category?: string;
    }>;
  }): void {
    this.sendEvent('add_to_cart', {
      currency: itemData.currency || 'USD',
      value: itemData.value,
      items: itemData.items
    });
  }

  /**
   * Track remove_from_cart event
   */
  public sendRemoveFromCartEvent(itemData: {
    currency?: string;
    value: number;
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity: number;
      item_category?: string;
    }>;
  }): void {
    this.sendEvent('remove_from_cart', {
      currency: itemData.currency || 'USD',
      value: itemData.value,
      items: itemData.items
    });
  }
}
